import { useState } from 'react';
import { Download, Trash2, ShieldCheck } from 'lucide-react';
import { API_ORIGIN, api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TimeOffSettings from './settings/TimeOffSettings';

export default function SettingsView() {
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete('/api/me');
      await refresh();
      navigate('/login', { replace: true });
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-1">Settings</h1>
        <p className="text-slate-500">Account and data controls.</p>
      </div>

      <section className="bg-white border border-slate-100 rounded-2xl p-6">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400 mb-3">Account</h2>
        <p className="font-medium">{me?.user.name}</p>
        <p className="text-sm text-slate-500">{me?.user.email}</p>
        <p className="text-xs text-slate-400 mt-2">
          Role: {me?.user.role} · Organization: {me?.organization?.name}
        </p>
      </section>

      {/*
       * Time-off admin settings — only rendered for OWNER + ADMIN. The API
       * itself enforces the same restriction (admin-only on the
       * /api/admin-config/* endpoints), so a MEMBER hitting those routes
       * directly would 403 anyway. Hiding the section is a UX nicety.
       */}
      {(me?.user.role === 'OWNER' || me?.user.role === 'ADMIN') && <TimeOffSettings />}

      <section className="bg-white border border-slate-100 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-emerald-600" />
          <h2 className="text-sm font-semibold uppercase tracking-widest text-slate-400">Your data (GDPR)</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Your data is stored in the EU (Frankfurt). You can export everything you've added at any time, or delete your account below.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href={`${API_ORIGIN}/api/me/export`}
            className="inline-flex items-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium"
          >
            <Download size={16} /> Export my data (JSON)
          </a>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              className="inline-flex items-center gap-2 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl px-4 py-2.5 text-sm font-medium"
            >
              <Trash2 size={16} /> Delete my account
            </button>
          ) : (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5">
              <span className="text-sm text-rose-700">Permanently delete? Data is purged after 30 days.</span>
              <button onClick={handleDelete} disabled={deleting} className="text-sm font-semibold text-rose-700 hover:underline disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button onClick={() => setConfirming(false)} className="text-sm text-slate-500">Cancel</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
